import Daily from '@daily-co/daily-js'
import { useAskVisio } from '@gozoki/api'
import type { VisioLink } from '@gozoki/api-types'
import * as Sentry from '@sentry/react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { VisioContext } from '../utils/context/visio.context'
import VideoCall, { type VisioPhase } from './VideoCall'

// Borne uniquement l'établissement de l'appel (requête API + connexion à la salle Daily).
// Une fois connecté, l'appel reste ouvert jusqu'au clic sur « Quitter » : la caisse peut
// mettre plus de temps à rejoindre, et elle n'envoie pas toujours de flux vidéo.
const CALL_SETUP_TIMEOUT_MS = 12_000

const VisioComponent = () => {
  const {
    shouldStartCall,
    startCall,
    kioskId,
    storeId,
    includeVideo,
    openingEventId,
    endCall,
    setCallObject,
    clearCallObject,
    isCallInProgress,
  } = useContext(VisioContext)
  const { mutate: askVisio } = useAskVisio()
  const [phase, setPhase] = useState<VisioPhase>('starting')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const requestStartedAt = useRef(0)
  const requestAttempt = useRef(0)
  const requestAbortController = useRef<AbortController | null>(null)

  useEffect(
    () => () => {
      requestAttempt.current += 1
      requestAbortController.current?.abort()
      clearCallObject()
    },
    [clearCallObject],
  )

  const addTimingBreadcrumb = useCallback(
    (milestone: 'clicked' | 'api-ready' | 'joined' | 'remote-video-playing') => {
      const durationMs = requestStartedAt.current
        ? Math.round(performance.now() - requestStartedAt.current)
        : 0
      Sentry.addBreadcrumb({
        category: 'visio.timing',
        data: {
          durationMs,
          kioskId,
          storeId,
        },
        level: 'info',
        message: milestone,
      })
      if (milestone !== 'clicked') {
        Sentry.metrics.distribution(`visio.${milestone}.duration`, durationMs, {
          attributes: { kioskId: String(kioskId), storeId: String(storeId) },
          unit: 'millisecond',
        })
      }
    },
    [kioskId, storeId],
  )

  const joinRoom = useCallback(
    (visio: VisioLink, attempt: number) => {
      if (attempt !== requestAttempt.current) return
      const freshCallObject = Daily.createCallObject()
      const handleUnexpectedEnd = () => {
        if (attempt !== requestAttempt.current) return
        requestAttempt.current += 1
        clearCallObject()
        setErrorMessage("L'appel vidéo a été interrompu.")
        setPhase('error')
      }
      freshCallObject.on('left-meeting', handleUnexpectedEnd)
      freshCallObject.on('error', handleUnexpectedEnd)
      setCallObject(freshCallObject)
      setPhase('connecting')

      void freshCallObject
        .join({
          startVideoOff: !includeVideo,
          token: visio.token,
          url: visio.url,
        })
        .then(() => {
          if (attempt !== requestAttempt.current) return
          addTimingBreadcrumb('joined')
          setPhase('live')
        })
        .catch(() => {
          if (attempt !== requestAttempt.current) return
          clearCallObject()
          setErrorMessage('La connexion à la salle vidéo a échoué.')
          setPhase('error')
        })
    },
    [addTimingBreadcrumb, clearCallObject, includeVideo, setCallObject],
  )

  const requestVisio = useCallback(() => {
    if (kioskId === null) return

    requestAbortController.current?.abort()
    const abortController = new AbortController()
    requestAbortController.current = abortController
    const attempt = ++requestAttempt.current
    clearCallObject()
    requestStartedAt.current = performance.now()
    setErrorMessage(null)
    setPhase('starting')
    addTimingBreadcrumb('clicked')
    askVisio(
      {
        kioskId,
        includeVideo,
        openingEventId: openingEventId ?? undefined,
        signal: abortController.signal,
      },
      {
        onSuccess: (visio) => {
          if (attempt !== requestAttempt.current) return
          requestAbortController.current = null
          addTimingBreadcrumb('api-ready')
          joinRoom(visio, attempt)
        },
        onError: (error) => {
          if (attempt !== requestAttempt.current) return
          requestAbortController.current = null
          setErrorMessage(error.message || "Impossible de démarrer l'appel.")
          setPhase('error')
        },
      },
    )
  }, [
    addTimingBreadcrumb,
    askVisio,
    clearCallObject,
    includeVideo,
    joinRoom,
    kioskId,
    openingEventId,
  ])

  useEffect(() => {
    if (!shouldStartCall || kioskId === null) return
    startCall()
    requestVisio()
  }, [kioskId, requestVisio, shouldStartCall, startCall])

  useEffect(() => {
    if (!isCallInProgress || (phase !== 'starting' && phase !== 'connecting')) return
    const elapsed = performance.now() - requestStartedAt.current
    const timeout = window.setTimeout(
      () => {
        requestAttempt.current += 1
        requestAbortController.current?.abort()
        requestAbortController.current = null
        clearCallObject()
        setErrorMessage("L'appel n'a pas pu être établi dans les 12 secondes.")
        setPhase('timeout')
      },
      Math.max(0, CALL_SETUP_TIMEOUT_MS - elapsed),
    )
    return () => window.clearTimeout(timeout)
  }, [clearCallObject, isCallInProgress, phase])

  const handleRemoteVideoPlayable = useCallback(() => {
    addTimingBreadcrumb('remote-video-playing')
  }, [addTimingBreadcrumb])

  const handleLeave = useCallback(() => {
    requestAttempt.current += 1
    requestAbortController.current?.abort()
    requestAbortController.current = null
    endCall()
  }, [endCall])

  if (!shouldStartCall && !isCallInProgress) return null

  return (
    <VideoCall
      phase={phase}
      errorMessage={errorMessage}
      leaveCall={handleLeave}
      onRemoteVideoPlayable={handleRemoteVideoPlayable}
      onRetry={requestVisio}
    />
  )
}

export default VisioComponent
